/**
 * UserProfile Component
 *
 * A luxury-styled user profile popover that adapts based on plant type.
 *
 * Features:
 * - JLR luxury styling with glass-morphism effects
 * - User avatar with gradient backgrounds
 * - Session information display (for JLR plants)
 * - Elegant logout functionality
 * - Dark/Light mode adaptation
 * - Responsive design
 *
 * Usage:
 * - Triggered from Header component user avatar click
 * - Displays user info and logout option
 * - Clears all localStorage on logout
 *
 * Plant-specific Features:
 * - JLR: Premium styling with accent borders and session info
 * - Standard: Clean professional appearance
 */

import { PersonOutline } from '@mui/icons-material';
import {
	Button,
	Paper,
	Popover,
	Box,
	Typography,
	Avatar,
	Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { JLR_PLANT, DARK_MODE } from '../../constants';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import { useDispatch } from 'react-redux';
import { logout } from '../../redux/actionCreators/user';

const UserProfile = ({
	open,
	handleClose,
	anchorEl,
	user,
	updateUser,
	resetFilterTable,
	updateLastLineItem,
	themeMode,
}) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const isJlr = user.plant === JLR_PLANT;
	const isDarkMode = themeMode === DARK_MODE;
	const { showMessageOverlay } = useMessageContextManager();
	const dispatch = useDispatch();

	/**
	 * Handles user logout process
	 *
	 * Steps:
	 * 1. Clear all localStorage items (user data, operations, line items)
	 * 2. Reset Redux state (filter table, line items, user data)
	 * 3. Navigate back to login page
	 *
	 * This ensures complete session cleanup for security
	 */
	const logoutUser = () => {
		const successCallback = (data) => {
			// Clear specific localStorage items
			localStorage.removeItem('user');
			localStorage.removeItem('operations');
			localStorage.removeItem('currentLineItem');
			localStorage.removeItem('lastLineItem');
			// Clear any remaining localStorage data
			localStorage.clear();

			// Reset Redux state
			resetFilterTable();
			updateLastLineItem([{}]);
			updateUser({
				token: '',
				plant: { name: '' },
				line: { name: '' },
				station: { name: '' },
			});
			const closeSuccessOverlay = showMessageOverlay({
				success: true,
				contextText: 'Success',
				info: data,
			});

			setTimeout(() => {
				if (typeof closeSuccessOverlay === 'function') closeSuccessOverlay();

				// Navigate to login
				navigate('/login');
			}, 2000);
		};

		const errorCallback = (error) => {
			showMessageOverlay({
				error: true,
				contextText: 'Logout Failed',
				info: error,
			});
		};
		dispatch(logout(successCallback, errorCallback));
	};

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={handleClose}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			slotProps={{
				paper: {
					elevation: 0,
					sx: {
						minWidth: {
							md: 280,
							lg: 500,
						},
						padding: 0,
						mt: 1,
					},
				},
			}}
		>
			<Paper
				sx={{
					background: isJlr
						? isDarkMode
							? 'linear-gradient(135deg, #1a1d20, #2d3136)'
							: 'linear-gradient(135deg, #ffffff, #fafbfc)'
						: theme.palette.background.paper,
					border: isJlr
						? `1px solid ${
								isDarkMode
									? 'rgba(255, 255, 255, 0.1)'
									: 'rgba(45, 74, 45, 0.1)'
						  }`
						: `1px solid ${theme.palette.divider}`,
					borderRadius: isJlr ? '16px' : '8px',
					overflow: 'hidden',
					position: 'relative',

					// JLR accent border
					...(isJlr && {
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							height: '3px',
							background: isDarkMode
								? 'linear-gradient(90deg, #0066cc, #c8b99c)'
								: 'linear-gradient(90deg, #2d4a2d, #c8b99c)',
						},
					}),
				}}
			>
				<Box sx={{ p: 3, pt: isJlr ? 4 : 3 }}>
					{/* Profile Header */}
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
						<Avatar
							sx={{
								width: {
									md: isJlr ? 56 : 48,
									lg: isJlr ? 100 : 84,
								},
								height: {
									md: isJlr ? 56 : 48,
									lg: isJlr ? 100 : 84,
								},
								mr: 2,
								background: isJlr
									? isDarkMode
										? 'linear-gradient(135deg, #0066cc, #004499)'
										: 'linear-gradient(135deg, #2d4a2d, #1a2d1a)'
									: theme.palette.primary.main,
								border: isJlr
									? `2px solid ${
											isDarkMode
												? 'rgba(0, 102, 204, 0.2)'
												: 'rgba(45, 74, 45, 0.2)'
									  }`
									: 'none',
							}}
						>
							<PersonOutline
								sx={{
									fontSize: {
										md: isJlr ? 28 : 24,
										lg: isJlr ? 52 : 44,
									},
									color: '#ffffff',
								}}
							/>
						</Avatar>
						<Box sx={{ flex: 1 }}>
							<Typography
								variant="h6"
								sx={{
									fontFamily: isJlr
										? "'Inter', sans-serif"
										: theme.typography.fontFamily,
									fontWeight: isJlr ? 600 : 500,
									color: isJlr
										? isDarkMode
											? '#ffffff'
											: '#2d4a2d'
										: theme.palette.text.primary,
									mb: 0.5,
									fontSize: {
										md: isJlr ? '1.125rem' : '1rem',
										lg: isJlr ? '2.125rem' : '2rem',
									},
								}}
							>
								{user.username}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontFamily: isJlr
										? "'Inter', sans-serif"
										: theme.typography.fontFamily,
									color: isJlr
										? isDarkMode
											? 'rgba(255, 255, 255, 0.7)'
											: 'rgba(45, 74, 45, 0.7)'
										: theme.palette.text.secondary,
									fontSize: {
										md: isJlr ? '0.875rem' : '0.8125rem',
										lg: isJlr ? '1.875rem' : '1.8125rem',
									},
								}}
							>
								{`${user.username}@tatamotors.com`}
							</Typography>
						</Box>
					</Box>

					{/* Plant & Station Info */}
					{isJlr && (
						<>
							<Divider
								sx={{
									my: 2,
									borderColor: isDarkMode
										? 'rgba(255, 255, 255, 0.1)'
										: 'rgba(45, 74, 45, 0.1)',
								}}
							/>
							<Box sx={{ mb: 2 }}>
								<Typography
									variant="caption"
									sx={{
										fontFamily: "'Inter', sans-serif",
										color: isDarkMode
											? 'rgba(255, 255, 255, 0.6)'
											: 'rgba(45, 74, 45, 0.6)',
										textTransform: 'uppercase',
										letterSpacing: '0.5px',
										fontWeight: 500,
									}}
								>
									Current Session
								</Typography>
								<Typography
									variant="body2"
									sx={{
										fontFamily: "'Inter', sans-serif",
										color: isDarkMode
											? 'rgba(255, 255, 255, 0.9)'
											: 'rgba(45, 74, 45, 0.9)',
										mt: 0.5,
									}}
								>
									{[user.plant, user.station].filter(Boolean).join(' • ')}
								</Typography>
							</Box>
						</>
					)}

					{/* Logout Button */}
					<Button
						fullWidth
						variant={isJlr ? 'outlined' : 'contained'}
						startIcon={<PowerSettingsNewIcon />}
						onClick={logoutUser}
						sx={{
							mt: 1,
							fontFamily: isJlr
								? "'Inter', sans-serif"
								: theme.typography.fontFamily,
							fontWeight: isJlr ? 600 : 500,
							borderRadius: isJlr ? '12px' : '8px',
							padding: {
								md: isJlr ? '12px 20px' : '8px 16px',
								lg: isJlr ? '20px 26px' : '12px 20px',
							},
							fontSize: {
								md: isJlr ? '0.875rem' : '0.8125rem',
								lg: isJlr ? '1.825rem' : '1.8125rem',
							},
							textTransform: 'none',
							letterSpacing: isJlr ? '0.5px' : 'normal',

							...(isJlr && {
								color: isDarkMode ? '#ffffff' : '#2d4a2d',
								borderColor: isDarkMode
									? 'rgba(255, 255, 255, 0.2)'
									: 'rgba(45, 74, 45, 0.2)',

								'&:hover': {
									borderColor: isDarkMode ? '#ffffff' : '#2d4a2d',
									background: isDarkMode
										? 'rgba(255, 255, 255, 0.05)'
										: 'rgba(45, 74, 45, 0.05)',
									transform: 'translateY(-1px)',
								},
							}),
						}}
					>
						Sign Out
					</Button>
				</Box>
			</Paper>
		</Popover>
	);
};

UserProfile.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	anchorEl: PropTypes.instanceOf(Element),
	updateUser: PropTypes.func.isRequired,
	resetFilterTable: PropTypes.func.isRequired,
};

export default UserProfile;
